import React from 'react';
import { motion } from 'framer-motion';
import { Cookie, ArrowLeft, Settings, BarChart2, Shield, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';

const CookiePolicyPage = () => {
  const lastUpdate = "19 de febrero de 2026";
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Link to="/" className="inline-flex items-center text-slate-400 hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al inicio
          </Link>
          
          <div className="flex items-center space-x-4 mb-4">
            <div className="p-3 bg-amber-500/20 rounded-xl">
              <Cookie className="w-8 h-8 text-amber-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Política de Cookies</h1>
              <p className="text-slate-400">Última actualización: {lastUpdate}</p>
            </div>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700 space-y-8"
        >
          {/* Introducción */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">1. ¿Qué son las Cookies?</h2>
            <p className="text-slate-300 leading-relaxed">
              Las cookies son pequeños archivos de texto que se almacenan en su dispositivo cuando 
              visita nuestro sitio web. Nos permiten recordar sus preferencias, mejorar su experiencia 
              de navegación y analizar cómo utiliza nuestra plataforma. Esta política cumple con la 
              <strong> Ley N° 19.496 sobre Protección de los Derechos de los Consumidores</strong> de Chile.
            </p>
          </section>

          {/* Tipos de Cookies */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">2. Tipos de Cookies que Utilizamos</h2>
            
            <div className="space-y-4">
              {/* Cookies Esenciales */}
              <div className="bg-slate-700/50 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-emerald-500/20 rounded-lg">
                    <Shield className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-emerald-400">Cookies Esenciales</h3>
                    <span className="text-xs text-slate-400">Siempre activas</span>
                  </div>
                </div>
                <p className="text-slate-300 text-sm mb-3">
                  Necesarias para el funcionamiento básico del sitio. Sin ellas, no podríamos 
                  proveer nuestros servicios.
                </p>
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-400 text-left">
                        <th className="pb-2">Nombre</th>
                        <th className="pb-2">Propósito</th>
                        <th className="pb-2">Duración</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-300">
                      <tr>
                        <td className="py-1 font-mono text-xs">notary_token</td>
                        <td className="py-1">Autenticación de sesión</td>
                        <td className="py-1">7 días</td>
                      </tr>
                      <tr>
                        <td className="py-1 font-mono text-xs">notaryos_theme</td>
                        <td className="py-1">Preferencia de tema</td>
                        <td className="py-1">1 año</td>
                      </tr>
                      <tr>
                        <td className="py-1 font-mono text-xs">cookie_consent</td>
                        <td className="py-1">Registro de consentimiento</td>
                        <td className="py-1">1 año</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Cookies de Rendimiento */}
              <div className="bg-slate-700/50 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-blue-500/20 rounded-lg">
                    <BarChart2 className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-blue-400">Cookies de Rendimiento</h3>
                    <span className="text-xs text-slate-400">Opcional - Con su consentimiento</span>
                  </div>
                </div>
                <p className="text-slate-300 text-sm mb-3">
                  Nos ayudan a entender cómo los visitantes interactúan con nuestro sitio, 
                  permitiéndonos mejorar continuamente la experiencia de usuario.
                </p>
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-400 text-left">
                        <th className="pb-2">Nombre</th>
                        <th className="pb-2">Propósito</th>
                        <th className="pb-2">Duración</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-300">
                      <tr>
                        <td className="py-1 font-mono text-xs">_analytics_id</td>
                        <td className="py-1">Análisis de uso</td>
                        <td className="py-1">2 años</td>
                      </tr>
                      <tr>
                        <td className="py-1 font-mono text-xs">_session_data</td>
                        <td className="py-1">Métricas de sesión</td>
                        <td className="py-1">30 min</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Cookies de Preferencias */}
              <div className="bg-slate-700/50 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-purple-500/20 rounded-lg">
                    <Settings className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-purple-400">Cookies de Preferencias</h3>
                    <span className="text-xs text-slate-400">Opcional - Con su consentimiento</span>
                  </div>
                </div>
                <p className="text-slate-300 text-sm mb-3">
                  Permiten que el sitio recuerde sus elecciones y proporcione características 
                  personalizadas mejoradas.
                </p>
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-400 text-left">
                        <th className="pb-2">Nombre</th>
                        <th className="pb-2">Propósito</th>
                        <th className="pb-2">Duración</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-300">
                      <tr>
                        <td className="py-1 font-mono text-xs">recent_searches</td>
                        <td className="py-1">Búsquedas recientes</td>
                        <td className="py-1">30 días</td>
                      </tr>
                      <tr>
                        <td className="py-1 font-mono text-xs">notaryos_tour_completed</td>
                        <td className="py-1">Estado del tour</td>
                        <td className="py-1">Permanente</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Cookies de Terceros */}
              <div className="bg-slate-700/50 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="p-2 bg-orange-500/20 rounded-lg">
                    <Globe className="w-5 h-5 text-orange-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-orange-400">Cookies de Terceros</h3>
                    <span className="text-xs text-slate-400">Opcional - Con su consentimiento</span>
                  </div>
                </div>
                <p className="text-slate-300 text-sm mb-3">
                  Utilizadas por servicios externos integrados en nuestra plataforma.
                </p>
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-400 text-left">
                        <th className="pb-2">Proveedor</th>
                        <th className="pb-2">Propósito</th>
                        <th className="pb-2">Más info</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-300">
                      <tr>
                        <td className="py-1">Stripe</td>
                        <td className="py-1">Procesamiento de pagos</td>
                        <td className="py-1">
                          <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" 
                             className="text-blue-400 hover:underline text-xs">Ver política</a>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>

          {/* Gestión de Cookies */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">3. Cómo Gestionar sus Cookies</h2>
            <p className="text-slate-300 mb-4">
              Puede gestionar sus preferencias de cookies de las siguientes maneras:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>
                <strong>Banner de Cookies:</strong> Al acceder por primera vez, puede aceptar o rechazar 
                las cookies opcionales mediante nuestro banner de consentimiento.
              </li>
              <li>
                <strong>Panel de Preferencias:</strong> En cualquier momento puede acceder al panel de 
                configuración de cookies desde el enlace en el pie de página.
              </li>
              <li>
                <strong>Configuración del Navegador:</strong> Puede configurar su navegador para bloquear 
                o eliminar cookies. Consulte la ayuda de su navegador para más información.
              </li>
            </ul>
            <div className="mt-4 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
              <p className="text-amber-200 text-sm">
                <strong>Nota:</strong> Bloquear las cookies esenciales puede afectar el funcionamiento 
                de la plataforma, incluyendo la imposibilidad de iniciar sesión o guardar contratos.
              </p>
            </div>
          </section>

          {/* Base Legal */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">4. Base Legal</h2>
            <p className="text-slate-300 leading-relaxed">
              El uso de cookies en NotaryOS se fundamenta en:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4 mt-3">
              <li>
                <strong>Cookies esenciales:</strong> Interés legítimo para proveer el servicio 
                solicitado (Art. 4 Ley 19.628).
              </li>
              <li>
                <strong>Cookies opcionales:</strong> Su consentimiento expreso e informado, 
                que puede retirar en cualquier momento.
              </li>
            </ul>
          </section>

          {/* Actualizaciones */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">5. Actualizaciones de esta Política</h2>
            <p className="text-slate-300 leading-relaxed">
              Podemos actualizar esta Política de Cookies periódicamente para reflejar cambios en 
              nuestras prácticas o por requisitos legales. Le notificaremos cualquier cambio 
              significativo mediante un aviso en nuestra plataforma. Le recomendamos revisar esta 
              política regularmente.
            </p>
          </section>

          {/* Contacto */}
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">6. Contacto</h2>
            <p className="text-slate-300">
              Si tiene preguntas sobre nuestra política de cookies, contáctenos:
            </p>
            <div className="mt-4 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
              <p className="text-amber-400">
                <strong>Email:</strong> privacidad@notaryos.cl
              </p>
            </div>
          </section>
        </motion.div>

        {/* Footer */}
        <div className="mt-8 text-center text-slate-500 text-sm">
          © 2026 NotaryOS SpA. Todos los derechos reservados.
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;
